import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Mic, ChevronRight, ClipboardList } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { sectorStyle } from "@/lib/sectors";
import { ServiceRow } from "@/components/ServiceRow";
import { logUsage } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1700901742651-6b353164caf3?crop=entropy&cs=srgb&fm=jpg&q=70&w=900";

export const SearchBar = ({ initial = "", onSubmit }) => {
  const { t, setAssistantOpen, lang } = useApp();
  const [q, setQ] = useState(initial);
  return (
    <form role="search" onSubmit={(e) => { e.preventDefault(); onSubmit(q); }} className="flex items-center gap-2 rounded-2xl border border-border bg-card px-3 shadow-sm focus-within:ring-2 focus-within:ring-primary/30">
      <Search size={18} className="text-muted-foreground" aria-hidden="true" />
      <input data-testid="home-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} aria-label={t("search")}
        className="h-12 flex-1 bg-transparent text-sm outline-none" dir={lang === "ar" ? "rtl" : "ltr"} />
      <button type="button" data-testid="home-search-mic-btn" onClick={() => setAssistantOpen(true)} aria-label={t("assistant")} className="flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(var(--maroon-soft))] text-primary">
        <Mic size={18} />
      </button>
    </form>
  );
};

export const SectorGrid = ({ sectors }) => {
  const { secName, t } = useApp();
  return (
    <div className="grid grid-cols-3 gap-2.5">
      {sectors.map((s, i) => {
        const st = sectorStyle(s.sector_id);
        const Icon = st.icon;
        return (
          <Link key={s.sector_id} to={`/services?sector=${s.sector_id}`} data-testid={`sector-card-${st.slug}`}
            style={{ animationDelay: `${i * 35}ms`, background: st.color }}
            className="q1-fade-up group relative flex min-h-[104px] flex-col justify-between overflow-hidden rounded-2xl p-3 text-white shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md">
            <span className="absolute -end-4 -top-4 h-16 w-16 rounded-full bg-white/10 transition-transform group-hover:scale-125" aria-hidden="true" />
            <Icon size={24} aria-hidden="true" />
            <span>
              <span className="block text-[12px] font-semibold leading-tight">{secName(s)}</span>
              <span className="mt-0.5 block text-[10px] font-medium text-white/80" data-testid={`sector-count-${s.sector_id}`}>{s.services_count} {t("servicesCount")}</span>
            </span>
          </Link>
        );
      })}
    </div>
  );
};

export default function HomePage() {
  const { t, catalog, me, pLabel } = useApp();
  const nav = useNavigate();
  const popular = useMemo(() => (catalog?.services || []).filter((s) => s.popular), [catalog]);
  const go = (q) => { if (q.trim()) logUsage({ type: "search", screen: "home", value: q, mode: "typed" }); nav(`/services?q=${encodeURIComponent(q)}`); };
  return (
    <div className="space-y-6 px-4 pt-4" data-testid="home-page">
      <section className="relative overflow-hidden rounded-3xl text-white shadow-md">
        <img src={HERO} alt="Doha skyline" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#4a0b1f]/95 via-[#8A1538]/70 to-transparent" />
        <div className="relative p-5 pt-16">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#E9CB75]">{t("sandbox")}</p>
          <h1 className="mt-1 text-2xl font-bold leading-tight" data-testid="home-greeting">
            {t("welcome")}{me?.user ? `, ${me.user.name.split(" ")[0]}` : ""}</h1>
          <p className="mt-1 text-sm text-white/85">{t("tagline")}</p>
        </div>
      </section>
      <SearchBar onSubmit={go} />
      <Link to="/requests" data-testid="home-open-requests-card" className="flex items-center gap-3 rounded-2xl border border-[hsl(var(--accent)/0.35)] bg-[hsl(var(--gold-soft))] p-4 transition-shadow hover:shadow-md">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#B8901F] text-white"><ClipboardList size={20} /></span>
        <span className="flex-1">
          <span className="block text-sm font-semibold">{t("openRequests")}</span>
          <span className="text-xs text-muted-foreground">{pLabel(me?.user?.persona)}</span>
        </span>
        <span className="text-2xl font-extrabold text-primary" data-testid="home-open-requests-count">{me?.open_requests ?? 0}</span>
        <ChevronRight size={18} className="q1-flip text-muted-foreground" />
      </Link>
      <section>
        <h2 className="mb-3 text-base font-bold md:text-lg">{t("sectors")}</h2>
        {catalog ? <SectorGrid sectors={catalog.sectors} /> : <div className="h-64 animate-pulse rounded-2xl bg-muted" />}
      </section>
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-bold md:text-lg">{t("popular")}</h2>
          <Link to="/services" className="text-sm font-semibold text-primary" data-testid="popular-see-all-link">{t("seeAll")}</Link>
        </div>
        <div className="space-y-2" data-testid="popular-services-list">
          {popular.map((s, i) => <ServiceRow key={s.service_id} s={s} index={i} />)}
        </div>
      </section>
    </div>
  );
}
